/**
 * What a company may switch off (screen preference `nav.disabled`), what it
 * may never switch off, and which API route prefixes close with a switch.
 *
 * A company stores switch ids (MODULE_SWITCHES). A switch off removes its
 * nav entries from the menu (web `composeNav`), closes every route prefix
 * that names it (api `middleware/module-gating.ts`), and refuses the
 * assistant intents whose route equivalent it closes. Every switch closes
 * at least one prefix: nothing is offered that only hides a menu entry.
 */
import { isModuleKey } from "./modules.js";
/** The preference key of the company's switched-off modules. */
export const NAV_DISABLED_KEY = "nav.disabled";
/** The preference key of the items a user hides from their own menu. */
export const NAV_HIDDEN_KEY = "nav.hidden";
/** Code of the refusal of a request to a switched-off module. */
export const MODULE_DESACTIVE_CODE = "MODULE_DESACTIVE";
/** French message of that refusal. */
export const MODULE_DESACTIVE_MESSAGE = "Ce module est désactivé pour votre entreprise. Un administrateur peut le réactiver dans Paramètres › Modules et menus.";
/**
 * Nav entries no switch hides, and the API prefixes never gated. A switch
 * hiding one of these, or a gate under one of these prefixes, fails the
 * registry tests.
 */
export const NEVER_DISABLED_MODULES = [
    // Every role's landing falls back to /dashboard (role/resolve.ts
    // DASHBOARD_ROUTE), the one route open to every authenticated user.
    { key: "main", prefixes: [] },
    { key: "dashboard", prefixes: ["/api/dashboard"] },
    // Certified invoices, their verification and the MCF signature
    // arbitration: SFE routes and screens (release/sfe-scope.json).
    { key: "ventes-caisse", prefixes: [] },
    { key: "invoices", prefixes: ["/api/invoices"] },
    { key: "verification", prefixes: [] },
    { key: "mcf-signatures-en-doute", prefixes: ["/api/mcf"] },
    // The till issues certified receipts (SFE routes /api/pos). Refused, a
    // session left open would never reach its Z report. It reads the depots
    // and opens tables' carts through these two prefixes.
    { key: "pos", prefixes: ["/api/pos", "/api/draft-proposals/:id/pos-cart", "/api/stock/locations"] },
    // The stay checkout issues the certified invoice (web-billing).
    { key: "stays", prefixes: ["/api/hospitalite/stays"] },
    // The sales worker issues recurring invoices and sends reminders outside
    // any request (workers/sales.worker.ts); a switch-off would leave active
    // schedules running with no screen to stop them.
    { key: "sales-recurring", prefixes: [] },
    { key: "sales-reminders", prefixes: [] },
    // The invoice form and the till read clients and articles; the client
    // page lists the client's contacts.
    { key: "clients-crm", prefixes: [] },
    { key: "clients", prefixes: ["/api/clients", "/api/crm/contacts"] },
    { key: "catalogue", prefixes: [] },
    { key: "articles", prefixes: ["/api/articles"] },
    // Purchase orders, import cost sheets and the ledger all read suppliers.
    { key: "achats", prefixes: [] },
    { key: "suppliers", prefixes: ["/api/accounting/suppliers"] },
    // X, Z and A reports, the FEC and every other /api/reports route are
    // mounted from the SFE (SFE spec §2.12, §4, §5; Article 564 CGI).
    { key: "reports-hub", prefixes: [] },
    { key: "reports-fiscal", prefixes: ["/api/reports"] },
    // Settings hold this switch itself, the MCF devices and the bank
    // accounts; the sidebar reads the accounting regime on every page.
    { key: "admin", prefixes: [] },
    {
        key: "settings",
        prefixes: ["/api/settings", "/api/ui-preferences", "/api/accounting/bank-accounts", "/api/accounting/settings/accounting-system"],
    },
    // Who may issue and sign, per establishment (per-NIM numbering).
    { key: "users", prefixes: ["/api/users"] },
    { key: "roles", prefixes: ["/api/roles"] },
    { key: "establishments", prefixes: ["/api/establishments"] },
    // The audit journal holds the export journal (#533).
    { key: "audit", prefixes: ["/api/audit"] },
    // The subscription stays payable whatever is switched off.
    { key: "billing", prefixes: ["/api/billing"] },
];
const NEVER_DISABLED_KEYS = new Set(NEVER_DISABLED_MODULES.map((entry) => entry.key));
/** True when no switch may hide `key`. */
export function isNeverDisabled(key) {
    return NEVER_DISABLED_KEYS.has(key);
}
/** The ids a company can store in `nav.disabled`. */
export const MODULE_SWITCH_IDS = [
    "crm",
    "sales-quotes",
    "sales-delivery-notes",
    "hospitalite",
    "stock",
    "inventories",
    "purchase-orders",
    "import-costs",
    "supplier-invoices",
    "accounting",
    "declarations",
    "hr",
    "hr-payroll",
    "projects",
    "budget",
    "funding",
    "reporting",
    "cabinet",
    "webhooks",
    "erp-connection",
];
const group = (key) => ({ group: key });
const items = (key, ...itemKeys) => itemKeys.map((item) => ({ group: key, item }));
/** Every switch, in the order the settings screen lists them within a group. */
export const MODULE_SWITCHES = [
    { id: "crm", hides: items("clients-crm", "crm-leads", "crm-opportunities", "crm-activities") },
    { id: "sales-quotes", hides: items("ventes-caisse", "sales-quotes") },
    { id: "sales-delivery-notes", hides: items("ventes-caisse", "sales-delivery-notes") },
    { id: "hospitalite", hides: items("ventes-caisse", "tables", "menu", "stations", "rooms", "indicators") },
    { id: "stock", hides: items("catalogue", "stock", "depots", "inventories") },
    { id: "inventories", hides: items("catalogue", "inventories") },
    { id: "purchase-orders", hides: items("achats", "purchase-orders") },
    { id: "import-costs", hides: items("achats", "import-costs") },
    { id: "supplier-invoices", hides: items("achats", "supplier-invoices") },
    // The regime shows one of the two accounting groups, never both: one
    // switch closes the ledger for either. The screens built on the ledger
    // (VAT, liasse, supplier invoices, purchase orders) go with it.
    {
        id: "accounting",
        hides: [
            group("accounting"),
            group("smt"),
            ...items("fiscalite", "accounting-vat", "accounting-liasse-esintax"),
            ...items("achats", "supplier-invoices", "purchase-orders"),
            group("reporting"),
        ],
    },
    { id: "declarations", hides: items("fiscalite", "declarations-list", "declarations-calendar") },
    { id: "hr", hides: [group("hr")] },
    { id: "hr-payroll", hides: items("hr", "hr-payroll", "hr-payroll-parameters") },
    { id: "projects", hides: [group("projects")] },
    { id: "budget", hides: [group("budget")] },
    // Donor reports restate the funding agreements.
    { id: "funding", hides: [group("funding"), group("reporting")] },
    { id: "reporting", hides: [group("reporting")] },
    { id: "cabinet", hides: [group("cabinet")] },
    { id: "webhooks", hides: items("admin", "webhooks") },
    { id: "erp-connection", hides: items("admin", "erp-connection") },
];
/**
 * The route prefixes switches close, most specific first (the first match
 * wins). The NEVER_DISABLED_MODULES prefixes are matched before this table;
 * a prefix absent from both is never gated.
 */
export const MODULE_ROUTE_GATES = [
    { prefix: "/api/crm", closedBy: ["crm"] },
    { prefix: "/api/sales/quotes", closedBy: ["sales-quotes"] },
    // A quote drawn from a file read by the assistant.
    { prefix: "/api/draft-proposals/:id/quote", closedBy: ["sales-quotes"] },
    { prefix: "/api/sales/delivery-notes", closedBy: ["sales-delivery-notes"] },
    { prefix: "/api/hospitalite", closedBy: ["hospitalite"] },
    { prefix: "/api/stock/inventories", closedBy: ["stock", "inventories"] },
    { prefix: "/api/stock", closedBy: ["stock"] },
    // The purchase order form picks ledger accounts.
    { prefix: "/api/purchasing/purchase-orders", closedBy: ["purchase-orders", "accounting"] },
    { prefix: "/api/purchasing/import-costs", closedBy: ["import-costs"] },
    { prefix: "/api/accounting/supplier-invoices", closedBy: ["supplier-invoices", "accounting"] },
    { prefix: "/api/accounting", closedBy: ["accounting"] },
    // Imports that post to the ledger.
    { prefix: "/api/onboarding/import/opening-balance", closedBy: ["accounting"] },
    { prefix: "/api/onboarding/import/open-items", closedBy: ["accounting"] },
    { prefix: "/api/declarations", closedBy: ["declarations"] },
    { prefix: "/api/hr/payroll", closedBy: ["hr", "hr-payroll"] },
    { prefix: "/api/hr/imports/payroll-history", closedBy: ["hr", "hr-payroll"] },
    { prefix: "/api/hr", closedBy: ["hr"] },
    { prefix: "/api/projects", closedBy: ["projects"] },
    { prefix: "/api/budget", closedBy: ["budget"] },
    { prefix: "/api/funding", closedBy: ["funding"] },
    // Donor reports read the agreements and the ledger.
    { prefix: "/api/reporting", closedBy: ["reporting", "funding", "accounting"] },
    // The firm's own screens; /api/firm/links is the client company's
    // « Cabinet comptable » setting and stays open.
    { prefix: "/api/firm/firms", closedBy: ["cabinet"] },
    { prefix: "/api/firm/dashboard", closedBy: ["cabinet"] },
    { prefix: "/api/firm/dsf-visa", closedBy: ["cabinet"] },
    { prefix: "/api/webhooks", closedBy: ["webhooks"] },
    { prefix: "/api/admin/erp-connection", closedBy: ["erp-connection"] },
];
/** True when `url` starts with `prefix` on a segment boundary. */
export function underPrefix(url, prefix) {
    if (!url.startsWith(prefix))
        return false;
    const next = url.charAt(prefix.length);
    return next === "" || next === "/" || next === "?";
}
const NEVER_GATED_PREFIXES = NEVER_DISABLED_MODULES.flatMap((entry) => entry.prefixes);
/** The gate governing `url`, or null when no switch closes it. */
export function moduleGateForUrl(url) {
    if (NEVER_GATED_PREFIXES.some((prefix) => underPrefix(url, prefix)))
        return null;
    return MODULE_ROUTE_GATES.find((gate) => underPrefix(url, gate.prefix)) ?? null;
}
/** True when one of the switches closing `gate` is off in `disabled`. */
export function gateRefuses(gate, disabled) {
    return gate.closedBy.some((id) => disabled.has(id));
}
/** True when a request to `url` is refused for a company that switched off `disabled`. */
export function moduleDisabledForUrl(url, disabled) {
    if (disabled.size === 0)
        return false;
    const gate = moduleGateForUrl(url);
    return gate !== null && gateRefuses(gate, disabled);
}
/** The nav entries removed while `disabled` switches are off: groups, and `group/item` pairs. */
export function hiddenNavEntries(disabled) {
    const off = new Set(disabled);
    const groups = new Set();
    const hiddenItems = new Set();
    for (const moduleSwitch of MODULE_SWITCHES) {
        if (!off.has(moduleSwitch.id))
            continue;
        for (const entry of moduleSwitch.hides) {
            if (entry.item === undefined)
                groups.add(entry.group);
            else
                hiddenItems.add(`${entry.group}/${entry.item}`);
        }
    }
    return { groups, items: hiddenItems };
}
/** The dashboard widgets whose data a switch closes. */
export const WIDGET_CLOSED_BY = {
    "stock-overview": ["stock"],
    "near-expiry": ["stock"],
    "stock-movements-today": ["stock"],
    treasury: ["accounting"],
    "kpi-margin": ["accounting"],
    "accounting-health": ["accounting"],
    "hr-headcount": ["hr"],
    "hr-leave": ["hr"],
    "hr-contracts": ["hr"],
    "payroll-status": ["hr", "hr-payroll"],
    "budget-execution": ["budget"],
    "budget-queues": ["budget"],
};
/** True when a switch in `disabled` closes the data of widget `key`. */
export function widgetSwitchedOff(key, disabled) {
    return (WIDGET_CLOSED_BY[key] ?? []).some((id) => disabled.has(id));
}
/**
 * The switches that close the accounting, firm and stock dashboards: every
 * widget of each reads routes under the prefixes these switches close
 * (`/api/accounting`, `/api/firm/*`, `/api/stock`).
 */
export const CATALOGUE_CLOSED_BY = {
    accounting: ["accounting"],
    firm: ["cabinet"],
    stock: ["stock"],
};
/** True when a switch in `disabled` closes the data of the widgets of dashboard `id`. */
export function catalogueWidgetSwitchedOff(id, disabled) {
    return CATALOGUE_CLOSED_BY[id].some((switchId) => disabled.has(switchId));
}
/** True when `value` is a switch id. */
export function isModuleSwitchId(value) {
    return MODULE_SWITCH_IDS.includes(value);
}
/**
 * A stored `nav.disabled` value as the switched-off switch ids, each once.
 * Anything else yields an empty list.
 */
export function navDisabledIds(value) {
    if (!Array.isArray(value))
        return [];
    const ids = [];
    for (const entry of value) {
        if (typeof entry === "string" && isModuleSwitchId(entry) && !ids.includes(entry))
            ids.push(entry);
    }
    return ids;
}
/** A stored `nav.hidden` value as the hidden item keys: known nav keys, each once. */
export function navHiddenIds(value) {
    if (!Array.isArray(value))
        return [];
    const keys = [];
    for (const entry of value) {
        if (typeof entry === "string" && isModuleKey(entry) && !keys.includes(entry))
            keys.push(entry);
    }
    return keys;
}
//# sourceMappingURL=company-modules.js.map